import { useEffect } from "react";

interface BlogHeadProps {
  title: string;
  description: string;
  slug: string;
  datePublished: string;
  dateModified: string;
  author?: string;
  cover?: string;
  tags?: string[];
}

export function BlogHead({ 
  title, 
  description, 
  slug, 
  datePublished, 
  dateModified, 
  author = "Cosmos Wave",
  cover,
  tags = []
}: BlogHeadProps) {
  useEffect(() => {
    // SEO Title (≤60 chars)
    const seoTitle = `${title} | Blog Cosmos Wave`;
    document.title = seoTitle;
    
    // Meta description (≤160 chars)
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }

    // Canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', `https://www.cosmos-wave.com/blog/${slug}`);
    }

    // Open Graph tags
    const ogType = document.querySelector('meta[property="og:type"]');
    if (ogType) ogType.setAttribute('content', 'article');

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', title);

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) ogDescription.setAttribute('content', description);

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', `https://www.cosmos-wave.com/blog/${slug}`);

    const ogImage = document.querySelector('meta[property="og:image"]');
    const imageUrl = cover || `https://www.cosmos-wave.com/og/blog/${slug}-1200x630.jpg`;
    if (ogImage) ogImage.setAttribute('content', imageUrl);

    // Twitter Card
    const twitterCard = document.querySelector('meta[name="twitter:card"]');
    if (twitterCard) twitterCard.setAttribute('content', 'summary_large_image');

    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) twitterTitle.setAttribute('content', title);

    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) twitterDescription.setAttribute('content', description);

    const twitterImage = document.querySelector('meta[name="twitter:image"]');
    if (twitterImage) twitterImage.setAttribute('content', imageUrl);

    // JSON-LD BlogPosting structured data
    const blogPostingScript = document.createElement('script');
    blogPostingScript.type = 'application/ld+json';
    blogPostingScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": title,
      "description": description,
      "image": imageUrl,
      "author": {
        "@type": "Organization",
        "name": author
      },
      "datePublished": datePublished,
      "dateModified": dateModified,
      "publisher": {
        "@type": "Organization",
        "name": "Cosmos Wave",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.cosmos-wave.com/icons/logo-512.png"
        }
      },
      "keywords": tags.join(', '),
      "url": `https://www.cosmos-wave.com/blog/${slug}`,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://www.cosmos-wave.com/blog/${slug}`
      }
    });

    // Breadcrumbs JSON-LD
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Accueil",
          "item": "https://www.cosmos-wave.com/"
        },
        {
          "@type": "ListItem", 
          "position": 2,
          "name": "Blog",
          "item": "https://www.cosmos-wave.com/blog"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": title,
          "item": `https://www.cosmos-wave.com/blog/${slug}`
        }
      ]
    });

    document.head.appendChild(blogPostingScript);
    document.head.appendChild(breadcrumbScript);

    // Staging safeguard script for robots meta
    const stagingScript = document.createElement('script');
    stagingScript.textContent = `
      (function(){
        var host=location.hostname;
        var isProd = (
          host === 'cosmos-wave.com' ||
          host === 'www.cosmos-wave.com' ||
          (!host.includes('lovable.app') && !host.includes('localhost'))
        );
        if(!isProd){
          var m=document.querySelector('meta[name="robots"]');
          if(!m){ m=document.createElement('meta'); m.name="robots"; document.head.appendChild(m); }
          m.content="noindex,nofollow";
        }
      })();
    `;
    document.head.appendChild(stagingScript);

    // Cleanup function
    return () => {
      document.head.removeChild(blogPostingScript);
      document.head.removeChild(breadcrumbScript);
      document.head.removeChild(stagingScript);
    };
  }, [title, description, slug, datePublished, dateModified, author, cover, tags]);

  return null;
}